import { InvalidUrlError } from '../../../common/errors/errors';
import { DEFAULT_KEY_CERTS_PAGE_SIZE } from '../../../common/keys/components/KeyCertificatesTable';
import { KeyCertificateMapper } from '../../../common/keys/mappers/KeyCertificateMapper';
import { KeyCertificate } from '../../../common/keys/models/KeyCertificate';
import { KEY_CERTS_FRAGMENT } from '../../../common/keys/services/KeyCertificatesService';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { DEFAULT_MATCHED_KEYS_PAGE_SIZE } from '../components/MatchedKeysTable';
import { OrganizationKeyDetailsMapper } from '../mappers/OrganizationKeyDetailsMapper';
import { OrganizationKeyMapper } from '../mappers/OrganizationKeyMapper';
import { ReportKeyMapper } from '../mappers/ReportKeyMapper';
import { OrganizationKey } from '../models/OrganizationKey';
import { OrganizationKeyDetails } from '../models/OrganizationKeyDetails';
import { ReportKey } from '../models/ReportKey';

const MATCHED_KEYS_FRAGMENT = `
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    id
    operationSummary
    report {
      id
      name
      project {
        id
        name
      }
    }
  }
}
totalCount
`;

// TODO(a/1204450248214838): Support sorters
export const GET_ORG_KEYS_QUERY = `
query(
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: OrganizationKeysFilter
) {
  viewer {
    organization {
      keys(
        first: $first,
        after: $after,
        last: $last,
        before: $before,
        filters: $filters
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            shortDescription
            information
            reportKeys {
              totalCount
            }
          }
        }
      }
    }
  }
}`;

export const GET_ORG_KEY_QUERY = `
query($keyId: ID!, $firstCertificates: Int, $firstReportKeys: Int) {
  key: node(id: $keyId) {
    ... on OrganizationKey {
      id
      valueJson
      metadata
      hasPrivateKey
      shortDescription
      certificates(first: $firstCertificates) {
        ${KEY_CERTS_FRAGMENT}
      }
      reportKeys(first: $firstReportKeys) {
        ${MATCHED_KEYS_FRAGMENT}
      }
    }
  }
}`;

export const GET_MATCHED_KEYS_QUERY = `
query ($keyId: ID!, $first: Int, $last: Int, $after: String, $before: String) {
  node(id: $keyId) {
    ... on OrganizationKey {
      reportKeys(first: $first, after: $after, last: $last, before: $before) {
        ${MATCHED_KEYS_FRAGMENT}
      }
    }
  }
}`;

export class OrganizationKeyService extends BaseGQLService {
  private readonly mapper = new OrganizationKeyMapper();
  private readonly keyDetailsMapper = new OrganizationKeyDetailsMapper();
  private readonly reportKeyMapper = new ReportKeyMapper();
  private readonly certificateMapper = new KeyCertificateMapper();

  async getList(cursor: CursorInfo, filters: FilterOperator | null): Promise<PaginationResult<OrganizationKey>> {
    return this.query(
      GET_ORG_KEYS_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        filters,
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.viewer.organization.keys;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  async getOrganizationKey(
    keyId: number
  ): Promise<{ key: OrganizationKeyDetails; initialMatchedKeys: PaginationResult<ReportKey>; initialCertificates: PaginationResult<KeyCertificate> }> {
    return this.query(
      GET_ORG_KEY_QUERY,
      {
        keyId: this.gqlIdEncoder.encode(ModelType.OrganizationKey, keyId),
        firstCertificates: DEFAULT_KEY_CERTS_PAGE_SIZE,
        firstReportKeys: DEFAULT_MATCHED_KEYS_PAGE_SIZE,
      },
      (data) => {
        if (!data.key) {
          throw new InvalidUrlError(`Invalid request: could not find OrganizationKey ${keyId}`);
        }
        const { reportKeys, certificates, ...key } = data.key;
        return {
          key: this.keyDetailsMapper.mapGQLModel(key),
          initialMatchedKeys: {
            pageInfo: reportKeys.pageInfo,
            totalCount: reportKeys.totalCount,
            data: reportKeys.edges.map((e: any) => this.reportKeyMapper.mapGQLModel(e.node)),
          },
          initialCertificates: {
            pageInfo: certificates.pageInfo,
            totalCount: certificates.totalCount,
            data: certificates.edges.map((e: any) => this.certificateMapper.mapGQLModel(e.node)),
          },
        };
      }
    );
  }

  async getMatchedKeys(keyId: number, cursor: CursorInfo, filters: FilterOperator | null): Promise<PaginationResult<ReportKey>> {
    return this.query(
      GET_MATCHED_KEYS_QUERY,
      {
        keyId: this.gqlIdEncoder.encode(ModelType.OrganizationKey, keyId),
        ...this.mapCursorInfo(cursor),
        filters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find OrganizationKey ${keyId}`);
        }
        const { edges: keys, pageInfo, totalCount } = data.node.reportKeys;
        return {
          data: keys.map((key: any) => this.reportKeyMapper.mapGQLModel(key.node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
